import { useCallback } from 'react';
import { startProxyAttendance } from '../../../../../../../../db/classes';
import { classFactory } from '../../../../../../../../db/classes/helpers';
import { AttendanceData, ClassStatus } from '../../../../../../../../db/classes/types';
import { courseFactory } from '../../../../../../../../db/courses/helpers';
import { CourseType } from '../../../../../../../../db/courses/types';
import { useCourseContext } from '../../../../../../../course/Context';
import { useClassContext } from '../../../../../../Context';
import { useAttendancePageContext } from '../../../../Context';
import { addStartAttendanceDataToClass } from '../../../../helpers';
import { AttendancePageScreens } from '../../../../types';
import { useAppContext } from '../../../../../../../../app/Context';
import registerInterceptors from '../../../../../../../../db/request-interceptors';

export interface UseRecordAttendanceDrawerSteps {
  goToNextStep: () => void;
}

const useRecordAttendanceDrawerSteps = ({ goToNextStep }: UseRecordAttendanceDrawerSteps) => {
  const { session, setSession, setError } = useAppContext();
  const { setCourse } = useCourseContext();
  const { setClass } = useClassContext();
  const { setCurrentScreen } = useAttendancePageContext();

  const handleNext = useCallback(
    async (data: {
      courseData: AttendanceData['inSession'][number]['courseData'];
      classData: AttendanceData['inSession'][number]['classData'][number];
    }) => {
      const { courseData, classData } = data;

      registerInterceptors({
        token: session?.token ?? null,
        cluster: session?.cluster ?? null,
        universitySlug: session?.university.slug ?? null,
        courseId: courseData.id,
        setError,
        setSession,
      });

      const courseDetails = courseFactory({
        courseId: courseData.id,
        code: courseData.code,
        enrolmentType: courseData.enrollmentType,
        joinCode: courseData.joinCode,
        title: courseData.title,
        canProxy: 1,
        courseType: CourseType.SYNCED,
        isPro: 1,
        courseTimezone: '',
      });
      setCourse(courseDetails);

      let classDetails = classFactory({
        classId: classData.classId,
        scheEndTime: classData.endTime,
        scheStartTime: classData.startTime,
        status: ClassStatus.IN_SESSION,
        classTimezone: '',
      });
      classDetails = {
        ...classDetails,
        attendance: {
          ...classDetails.attendance,
          numOfAttempts: classData.numAttempts,
          markedInStudents: classData.numPresent,
        },
      };
      setClass(classDetails);

      const response = await startProxyAttendance({ classId: classData.classId });

      setClass((prevClass) => {
        if (prevClass == null) return null;
        return addStartAttendanceDataToClass(prevClass, response);
      });

      setCurrentScreen(AttendancePageScreens.RECORDING_ATTENDANCE);
    },
    [
      session?.token,
      session?.cluster,
      session?.university.slug,
      setError,
      setCourse,
      setClass,
      setCurrentScreen,
    ]
  );

  return { handleNext };
};

export default useRecordAttendanceDrawerSteps;
